import { environment } from "../../../config/environment";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, Copy, RefreshCw } from "lucide-react";
import Sidebar from "../../Sidebar";
import { customFetch } from "../../../utils/Auth";

const COLUMNS = [
  { key: "timestamp", label: "Timestamp" },
  { key: "endpoint", label: "Endpoint" },
  { key: "org", label: "Org" },
  { key: "http_status", label: "Status" },
  { key: "error_message", label: "Error" },
  { key: "lambda_duration_ms", label: "Duration" },
  { key: "response_length", label: "Response Length" },
  { key: "request_body", label: "Request Body" },
  { key: "response_preview", label: "Response Preview" },
];

const formatJsonOneLine = (value) => {
  if (!value) return "";
  try {
    return JSON.stringify(JSON.parse(value));
  } catch {
    return String(value).replace(/\s+/g, " ").trim();
  }
};

const formatJsonForCopy = (value) => {
  if (!value) return "";
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return String(value);
  }
};

const PREVIEW_WIDTH = 280;
const PREVIEW_MAX_HEIGHT = 128;

const getPreviewPosition = (anchorEl) => {
  const rect = anchorEl.getBoundingClientRect();
  let top = rect.bottom + 4;
  let left = rect.left;

  if (top + PREVIEW_MAX_HEIGHT > window.innerHeight - 8) {
    top = Math.max(8, rect.top - PREVIEW_MAX_HEIGHT - 4);
  }
  if (left + PREVIEW_WIDTH > window.innerWidth - 8) {
    left = Math.max(8, window.innerWidth - PREVIEW_WIDTH - 8);
  }

  return { top, left };
};

const JsonHoverPreview = ({ preview, onKeepOpen, onClose }) => {
  if (!preview?.visible) return null;

  return createPortal(
    <div
      className="fixed z-[100] rounded border border-dark-purple/20 bg-light-purple shadow-lg ring-1 ring-dark-purple/10 overflow-hidden"
      style={{
        top: preview.top,
        left: preview.left,
        width: PREVIEW_WIDTH,
        maxHeight: PREVIEW_MAX_HEIGHT,
      }}
      onMouseEnter={onKeepOpen}
      onMouseLeave={onClose}
    >
      <pre className="m-0 p-2 text-[11px] leading-snug font-mono text-dark-purple/90 overflow-auto max-h-32 whitespace-pre-wrap break-words">
        {preview.text}
      </pre>
    </div>,
    document.body
  );
};

const CopyableTruncatedCell = ({
  value,
  onShowPreview,
  onHidePreview,
  formatDisplay,
  formatCopy,
  mono = true,
}) => {
  const [copied, setCopied] = useState(false);
  const cellRef = useRef(null);
  const displayText = formatDisplay(value);
  const copyText = formatCopy(value);

  if (!displayText) return "—";

  const handleCopy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(copyText);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const handleMouseEnter = () => {
    if (cellRef.current) {
      onShowPreview(copyText, cellRef.current);
    }
  };

  return (
    <div
      ref={cellRef}
      className="flex items-center gap-1 max-w-[15rem]"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={onHidePreview}
    >
      <span
        className={`truncate text-xs text-gray-600 flex-1 min-w-0 cursor-default ${
          mono ? "font-mono" : ""
        }`}
      >
        {displayText}
      </span>
      <button
        type="button"
        onClick={handleCopy}
        className="shrink-0 p-1 rounded text-gray-500 hover:text-dark-purple hover:bg-gray-100"
        title={copied ? "Copied" : "Copy to clipboard"}
        aria-label="Copy to clipboard"
      >
        {copied ? (
          <Check className="h-3.5 w-3.5 text-green-600" />
        ) : (
          <Copy className="h-3.5 w-3.5" />
        )}
      </button>
    </div>
  );
};

const formatDurationSeconds = (ms) => {
  if (ms == null || ms === "") return "—";
  const num = Number(ms);
  if (Number.isNaN(num)) return "—";
  return `${(num / 1000).toFixed(1)}s`;
};

const formatTimestamp = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
};

const statusClass = (status) => {
  if (status >= 500) return "text-white bg-red-600";
  if (status >= 400) return "text-red-900 bg-red-200";
  if (status >= 200 && status < 300) return "text-white bg-green-600";
  return "text-gray-700 bg-gray-200";
};

const FilterMultiSelect = ({ label, options, selected, onToggle, placeholder }) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const removeChip = (e, value) => {
    e.stopPropagation();
    onToggle(value);
  };

  return (
    <div ref={containerRef} className="relative flex-1 min-w-[220px] max-w-md">
      <span className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
        {label}
      </span>
      <div
        role="button"
        tabIndex={0}
        className={`flex flex-wrap items-center gap-1.5 min-h-[2.5rem] rounded-md border bg-white px-2 py-1.5 cursor-pointer transition-colors ${
          open ? "border-dark-purple ring-1 ring-dark-purple/20" : "border-gray-200 hover:border-gray-300"
        }`}
        onClick={() => setOpen((prev) => !prev)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen((prev) => !prev);
          }
        }}
      >
        {selected.length === 0 ? (
          <span className="text-sm text-gray-400 px-1 py-0.5">{placeholder}</span>
        ) : (
          selected.map((value) => (
            <span
              key={value}
              className="inline-flex items-center gap-1 rounded-full border border-dark-purple/20 bg-light-purple/40 text-dark-purple pl-2.5 pr-1 py-0.5 text-sm font-medium"
            >
              <span>{value}</span>
              <button
                type="button"
                aria-label={`Remove ${label} filter ${value}`}
                className="flex h-5 w-5 items-center justify-center rounded-full text-dark-purple/70 hover:bg-dark-purple/10 hover:text-dark-purple"
                onClick={(e) => removeChip(e, value)}
              >
                ×
              </button>
            </span>
          ))
        )}
        <span
          className={`ml-auto shrink-0 text-gray-400 text-xs px-1 transition-transform ${open ? "rotate-180" : ""}`}
          aria-hidden
        >
          ▼
        </span>
      </div>
      {open && (
        <div className="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto rounded-md bg-white shadow-lg border border-gray-200 p-2">
          {options.length === 0 ? (
            <p className="text-sm text-gray-500 px-2 py-1">No options available</p>
          ) : (
            options.map((option) => (
              <label
                key={option}
                className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selected.includes(option)}
                  onChange={() => onToggle(option)}
                  className="rounded border-gray-300 text-dark-purple focus:ring-dark-purple"
                />
                <span className="text-sm text-dark-purple">{option}</span>
              </label>
            ))
          )}
        </div>
      )}
    </div>
  );
};

const PAGE_SIZE = 100;

const SavingsFootnote = ({ stats, loading }) => {
  if (loading) {
    return <span className="text-xs text-gray-400">…</span>;
  }
  if (!stats) return null;
  return (
    <span className="text-xs text-gray-400">
      ${stats.estimatedSavings.toFixed(1)} saved
    </span>
  );
};

const OncoEMR = ({ setAuth, setUserOrg }) => {
  const [items, setItems] = useState([]);
  const [filterOptions, setFilterOptions] = useState({ endpoints: [], orgs: [] });
  const [selectedEndpoints, setSelectedEndpoints] = useState([]);
  const [selectedOrgs, setSelectedOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [jsonPreview, setJsonPreview] = useState(null);
  const [pageCursors, setPageCursors] = useState([null]);
  const [pageIndex, setPageIndex] = useState(0);
  const [nextCursor, setNextCursor] = useState(null);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const hidePreviewTimer = useRef(null);

  const hasFilters = selectedEndpoints.length > 0 || selectedOrgs.length > 0;

  const showJsonPreview = useCallback((text, anchorEl) => {
    if (hidePreviewTimer.current) {
      clearTimeout(hidePreviewTimer.current);
      hidePreviewTimer.current = null;
    }
    const { top, left } = getPreviewPosition(anchorEl);
    setJsonPreview({ visible: true, text, top, left });
  }, []);

  const hideJsonPreview = useCallback(() => {
    hidePreviewTimer.current = setTimeout(() => {
      setJsonPreview(null);
      hidePreviewTimer.current = null;
    }, 60);
  }, []);

  const keepJsonPreviewOpen = useCallback(() => {
    if (hidePreviewTimer.current) {
      clearTimeout(hidePreviewTimer.current);
      hidePreviewTimer.current = null;
    }
  }, []);

  useEffect(
    () => () => {
      if (hidePreviewTimer.current) clearTimeout(hidePreviewTimer.current);
    },
    []
  );

  const buildLogUrl = useCallback(
    (cursor) => {
      const params = new URLSearchParams({ limit: String(PAGE_SIZE) });
      if (cursor) params.set("cursor", cursor);
      if (selectedEndpoints.length) {
        params.set("endpoints", selectedEndpoints.join(","));
      }
      if (selectedOrgs.length) {
        params.set("orgs", selectedOrgs.join(","));
      }
      return `${environment.apiUrl}/integrations/oncoemr/api-call-log?${params.toString()}`;
    },
    [selectedEndpoints, selectedOrgs]
  );

  const fetchFilterOptions = useCallback(async () => {
    const response = await customFetch(
      `${environment.apiUrl}/integrations/oncoemr/api-call-log/filter-options`,
      {
        method: "GET",
        headers: { token: localStorage.getItem("token") },
      }
    );
    if (!response.ok) {
      throw new Error(`Failed to fetch filter options (${response.status})`);
    }
    const data = await response.json();
    setFilterOptions({
      endpoints: data.endpoints || [],
      orgs: data.orgs || [],
    });
  }, []);

  const fetchPage = useCallback(
    async (cursor, { isRefresh = false } = {}) => {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);
      try {
        const response = await customFetch(buildLogUrl(cursor), {
          method: "GET",
          headers: { token: localStorage.getItem("token") },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch API call log (${response.status})`);
        }

        const data = await response.json();
        setItems(data.items || (Array.isArray(data) ? data : []));
        setNextCursor(data.nextCursor || null);
      } catch (err) {
        console.error("Error fetching OncoEMR API call log:", err);
        setError(err.message || "Failed to fetch API call log");
        setItems([]);
        setNextCursor(null);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [buildLogUrl]
  );

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const response = await customFetch(
        `${environment.apiUrl}/integrations/oncoemr/stats`,
        {
          method: "GET",
          headers: { token: localStorage.getItem("token") },
        }
      );
      if (response.ok) {
        setStats(await response.json());
      }
    } catch (err) {
      console.error("Error fetching OncoEMR stats:", err);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFilterOptions().catch((err) => {
      console.error("Error fetching filter options:", err);
    });
  }, [fetchFilterOptions]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const resetAndFetchFirstPage = useCallback(() => {
    setPageCursors([null]);
    setPageIndex(0);
    fetchPage(null);
  }, [fetchPage]);

  useEffect(() => {
    resetAndFetchFirstPage();
  }, [resetAndFetchFirstPage]);

  const goToNextPage = () => {
    if (!nextCursor) return;
    const newIndex = pageIndex + 1;
    setPageCursors((prev) => [...prev, nextCursor]);
    setPageIndex(newIndex);
    fetchPage(nextCursor);
  };

  const goToPreviousPage = () => {
    if (pageIndex === 0) return;
    const newIndex = pageIndex - 1;
    setPageIndex(newIndex);
    fetchPage(pageCursors[newIndex] ?? null);
  };

  const handleRefresh = () => {
    setPageCursors([null]);
    setPageIndex(0);
    fetchPage(null, { isRefresh: true });
    fetchStats();
  };

  const toggleEndpoint = (endpoint) => {
    setSelectedEndpoints((prev) =>
      prev.includes(endpoint)
        ? prev.filter((e) => e !== endpoint)
        : [...prev, endpoint]
    );
  };

  const toggleOrg = (org) => {
    setSelectedOrgs((prev) =>
      prev.includes(org) ? prev.filter((o) => o !== org) : [...prev, org]
    );
  };

  const clearFilters = () => {
    setSelectedEndpoints([]);
    setSelectedOrgs([]);
    // resetAndFetchFirstPage runs via effect when filters change
  };

  const renderCell = (item, key) => {
    const value = item[key];
    if (key === "timestamp") return formatTimestamp(value);
    if (key === "lambda_duration_ms") return formatDurationSeconds(value);
    if (key === "http_status") {
      if (value == null) return "—";
      return (
        <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${statusClass(value)}`}>
          {value}
        </span>
      );
    }
    if (key === "request_body" || key === "response_preview") {
      return (
        <CopyableTruncatedCell
          value={value}
          formatDisplay={formatJsonOneLine}
          formatCopy={formatJsonForCopy}
          onShowPreview={showJsonPreview}
          onHidePreview={hideJsonPreview}
        />
      );
    }
    if (key === "error_message") {
      if (value == null || String(value).trim() === "") return "—";
      return (
        <CopyableTruncatedCell
          value={value}
          formatDisplay={(v) => String(v).replace(/\s+/g, " ").trim()}
          formatCopy={(v) => String(v)}
          mono={false}
          onShowPreview={showJsonPreview}
          onHidePreview={hideJsonPreview}
        />
      );
    }
    if (value == null || value === "") return "—";
    return String(value);
  };

  return (
    <div className="w-full min-h-screen bg-white text-dark-purple font-sans flex">
      <Sidebar setAuth={setAuth} setUserOrg={setUserOrg} />
      <div
        className="flex flex-col gap-4 p-8"
        style={{
          marginLeft: "var(--sidebar-width, 250px)",
          width: "calc(100% - var(--sidebar-width, 250px))",
        }}
      >
        <div>
          <h1 className="text-3xl font-bold mb-1">OncoEMR</h1>
        </div>

        <div className="flex flex-wrap items-end gap-4">
          <FilterMultiSelect
            label="Endpoint"
            options={filterOptions.endpoints}
            selected={selectedEndpoints}
            onToggle={toggleEndpoint}
            placeholder="All endpoints"
          />
          <FilterMultiSelect
            label="Org"
            options={filterOptions.orgs}
            selected={selectedOrgs}
            onToggle={toggleOrg}
            placeholder="All orgs"
          />
          {hasFilters && (
            <button
              type="button"
              onClick={clearFilters}
              className="text-sm text-red-600 hover:text-red-700 underline shrink-0 mb-2.5"
            >
              Clear filters
            </button>
          )}
        </div>

        {loading && items.length === 0 && (
          <p className="opacity-70">Loading API call log…</p>
        )}

        {error && !refreshing && (
          <p className="text-red-600">{error}</p>
        )}

        {!loading && !error && items.length === 0 && (
          <div className="flex flex-col gap-3">
            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleRefresh}
                disabled={refreshing}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
                Refresh
              </button>
            </div>
            <p className="opacity-70">No API call log entries found.</p>
          </div>
        )}

        {items.length > 0 && (
          <div className={`flex flex-col gap-3 ${refreshing ? "opacity-60 pointer-events-none" : ""}`}>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleRefresh}
              disabled={refreshing}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
              Refresh
            </button>
          </div>
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
            <table className="min-w-full text-sm text-left">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {COLUMNS.map((col) => (
                    <th
                      key={col.key}
                      className="px-4 py-3 font-semibold text-gray-700 whitespace-nowrap"
                    >
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((item) => (
                  <tr key={`${item.endpoint}-${item.call_id}`} className="hover:bg-gray-50">
                    {COLUMNS.map((col) => (
                      <td key={col.key} className="px-4 py-2 text-gray-800 whitespace-nowrap">
                        {renderCell(item, col.key)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
            <div className="flex justify-end px-3 py-1.5 border-t border-gray-100 bg-gray-50/40">
              <SavingsFootnote stats={stats} loading={statsLoading} />
            </div>
          </div>
          <div className="flex items-center justify-between text-sm text-gray-600">
            <span>Page {pageIndex + 1}</span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={goToPreviousPage}
                disabled={pageIndex === 0 || loading || refreshing}
                className="px-3 py-1.5 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                type="button"
                onClick={goToNextPage}
                disabled={!nextCursor || loading || refreshing}
                className="px-3 py-1.5 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
          </div>
        )}
      </div>
      <JsonHoverPreview
        preview={jsonPreview}
        onKeepOpen={keepJsonPreviewOpen}
        onClose={hideJsonPreview}
      />
    </div>
  );
};

export default OncoEMR;
